import clsx from "clsx";
import type { UpdateProgramRequest } from "common/models/api/models/UpdateProgramRequest";
import FormScaffold from "ui/common/components/layout/FormScaffold/FormScaffold";
import useGetProgram from "features/programs/hooks/useGetProgram";
import useUpdateProgram from "features/programs/hooks/useUpdateProgram";
import { ProgramFormGridProps } from "./ProgramFormGrid.types";
import useProgramAddressAndBackButton from "./hooks/useProgramAddressAndBackButton";
import useProgramFormGridRows from "./hooks/useProgramFormGridRows";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { Operations } from "ui/admins/operations.types";
import { unflattenObject } from "ui/common/utils/unflatObject";

function ProgramFormGrid({ className, ...rest }: Readonly<ProgramFormGridProps>) {
    const { programAddress, shouldShowBackButton } = useProgramAddressAndBackButton();
    const { data: program } = useGetProgram({ address: programAddress });
    const { isLoading, isSuccess, mutate, reset } = useUpdateProgram();
    const rows = useProgramFormGridRows();
    const safeProgramAddress = programAddress;

    const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
        onSuccess: () => isSuccess,
        isLoading,
    });

    function handleOnSubmit(request: UpdateProgramRequest) {
        const unflattenRequest = unflattenObject(request);
        const executeAction = (...args: unknown[]) => {
            const [programData] = args as [{ address: string } & UpdateProgramRequest];
            mutate(programData);
        };

        const payload = {
            company: unflattenRequest.company,
            name: unflattenRequest.name,
            logo: unflattenRequest.logo,
            url: unflattenRequest.url,
            minAmount: unflattenRequest.minAmount,
            maxAmount: unflattenRequest.maxAmount,
        };

        verifyReleaseMethod({
            originalFn: executeAction,
            originalArgs: [{ address: safeProgramAddress, ...payload }],
            operationTypeId: Operations.MODIFY_PROGRAM_DATA,
        });
    }

    return (
        <FormScaffold
            back={shouldShowBackButton}
            className={clsx("program-form-grid", className)}
            isLoading={isLoading || isValidatingReleaseStrategy}
            isSuccess={isSuccess}
            onSubmit={handleOnSubmit}
            reset={reset}
            rows={rows}
            state={program}
            title={program?.name}
            {...rest}
        />
    );
}

export default ProgramFormGrid;
